import { styled, alpha } from '@mui/material/styles'
import { InputBase, IconButton, Fab } from '@mui/material'
import { colorPalette } from '../../style/colorPalette'

const FAB_SIZE = 56
const FAB_MARGIN = 24

/**
 * 起動ボタンの表示段階。
 * - visible: 常設状態。右下に全体が見えている
 * - peeking: 引っ込めた状態でカーソルが近づき、左半分だけ顔を出している
 * - hidden: 引っ込めた状態。画面外に完全に隠れている
 */
export type FabDisplayState = 'visible' | 'peeking' | 'hidden'

// 各段階での右方向の退避量。peeking はボタンの中心が画面右端に来る位置、
// hidden はボタンと余白の合計ぶん動かして画面外へ完全に追い出す位置。
const TRANSLATE_X_BY_DISPLAY_STATE: Record<FabDisplayState, number> = {
  visible: 0,
  peeking: FAB_MARGIN + FAB_SIZE / 2,
  hidden: FAB_MARGIN + FAB_SIZE,
}

// 起動ボタンと、その右上に重なる引っ込めボタンをまとめて動かす入れ物。
// 表示段階の切り替えは横方向のスライドで表現する。
export const FabContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'displayState',
})<{ displayState: FabDisplayState }>(({ displayState }) => ({
  position: 'fixed',
  bottom: FAB_MARGIN,
  right: FAB_MARGIN,
  zIndex: 1300,
  width: FAB_SIZE,
  height: FAB_SIZE,
  transform: `translateX(${TRANSLATE_X_BY_DISPLAY_STATE[displayState]}px)`,
  transition: 'transform 240ms ease',
  // 引っ込めている間は当たり判定も消し、画面右下に固定された他の要素
  // (WBS単票のディスカッション送信ボタン等)の操作を妨げないようにする。
  pointerEvents: displayState === 'hidden' ? 'none' : 'auto',
}))

// 画面右下のフローティング起動ボタン。位置は FabContainer が持つ。
export const ChatFab = styled(Fab)({
  // MUI Fab の既定 z-index(1050) は右上に重ねる DismissButton を覆ってしまう。
  // 重ね順は FabContainer(1300) が持つのでここでは不要。
  zIndex: 'auto',
  backgroundColor: colorPalette.skyBlue[7],
  color: colorPalette.monotone[0],
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[8],
  },
})

// 起動ボタンの右上に重ねる、AIチャットを画面外へ引っ込めるための小さなボタン。
export const DismissButton = styled(IconButton)({
  position: 'absolute',
  top: -2,
  right: -2,
  zIndex: 1,
  width: 20,
  height: 20,
  padding: 0,
  backgroundColor: colorPalette.monotone[4],
  color: colorPalette.monotone[0],
  boxShadow: '0 1px 4px rgba(36, 48, 80, 0.32)',
  '&:hover': {
    backgroundColor: colorPalette.monotone[5],
  },
})

// 起動ボタンの上に開くチャットパネル本体。
export const ChatPanel = styled('div')({
  position: 'fixed',
  bottom: 96,
  right: 24,
  zIndex: 1300,
  width: 760,
  maxWidth: 'calc(100vw - 48px)',
  height: 1040,
  maxHeight: 'calc(100vh - 140px)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: colorPalette.monotone[0],
  borderRadius: 12,
  border: `1px solid ${colorPalette.monotone[2]}`,
  boxShadow: '0 8px 24px rgba(36, 48, 80, 0.2)',
  overflow: 'hidden',
})

export const PanelHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  backgroundColor: colorPalette.monotone[1],
})

export const HeaderTitle = styled('span')({
  fontWeight: 600,
  fontSize: 14,
  color: colorPalette.monotone[10],
})

export const HeaderActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
})

export const InputArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 12,
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
})

export const ChatInput = styled(InputBase)({
  flexGrow: 1,
  backgroundColor: colorPalette.monotone[0],
  borderRadius: 4,
  padding: '8px 12px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  fontSize: 14,
  // hover で枠線が反応すると入力できると誤解させるため、無効時は除外する。
  '&:not(.Mui-disabled):hover': {
    borderColor: colorPalette.monotone[4],
  },
  '&.Mui-focused': {
    borderColor: colorPalette.skyBlue[7],
    boxShadow: `0 0 0 2px ${alpha(colorPalette.skyBlue[7], 0.2)}`,
  },
  // AI応答中は入力不可。MUI 既定では文字色しか変わらず入力できるように見えるため、
  // 背景をグレーに落としてカーソルでも操作不可であることを示す。
  '&.Mui-disabled': {
    backgroundColor: colorPalette.monotone[1],
    borderColor: colorPalette.monotone[2],
    cursor: 'not-allowed',
  },
  // input 要素にも指定しないと、テキスト領域の上ではカーソルが text のままになる。
  '& .MuiInputBase-input.Mui-disabled': {
    cursor: 'not-allowed',
  },
})

export const SendButton = styled(IconButton)({
  color: colorPalette.skyBlue[7],
  '&.Mui-disabled': {
    color: colorPalette.monotone[3],
  },
})

export const HeaderIconButton = styled(IconButton)({
  color: colorPalette.monotone[4],
  '&:hover': {
    color: colorPalette.monotone[6],
  },
  '&.Mui-disabled': {
    color: colorPalette.monotone[3],
  },
})

export const InlineError = styled('div')({
  padding: '4px 16px',
  color: colorPalette.pink[7],
  fontSize: 12,
})

// 送信前の画像添付プレビュー一覧。
export const AttachmentsPreviewRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  // 下側の余白を省くとサムネイルが入力エリアの境界線に接してしまう。
  padding: '8px 12px',
})

export const AttachmentPreviewItem = styled('div', {
  shouldForwardProp: prop => prop !== 'hasError',
})<{ hasError?: boolean }>(({ hasError }) => ({
  position: 'relative',
  width: 56,
  height: 56,
  borderRadius: 6,
  overflow: 'hidden',
  border: `1px solid ${hasError ? colorPalette.pink[5] : colorPalette.monotone[2]}`,
}))

export const AttachmentThumb = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
})

export const AttachmentUploadingOverlay = styled('div')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(colorPalette.monotone[10], 0.35),
  '&::after': {
    content: '""',
    width: 16,
    height: 16,
    border: `2px solid ${colorPalette.monotone[0]}`,
    borderTop: `2px solid ${colorPalette.skyBlue[7]}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})

export const RemoveAttachmentButton = styled(IconButton)({
  position: 'absolute',
  top: 0,
  right: 0,
  width: 18,
  height: 18,
  padding: 0,
  color: colorPalette.monotone[0],
  backgroundColor: alpha(colorPalette.monotone[10], 0.5),
  '&:hover': {
    backgroundColor: alpha(colorPalette.monotone[10], 0.7),
  },
})

// ファイルをドラッグしている間、パネル全体に重ねてドロップ可能であることを示す。
export const DropOverlay = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(colorPalette.skyBlue[7], 0.12),
  border: `2px dashed ${colorPalette.skyBlue[7]}`,
  borderRadius: 12,
  color: colorPalette.skyBlue[8],
  fontSize: 14,
  fontWeight: 600,
  // オーバーレイ自身がドラッグ対象になると dragleave が誤発火し、表示がちらつく。
  pointerEvents: 'none',
})
